/**
 * `prompt` for the sign-in's start (contract §13): left out, the server tries silently first (`none`, §10) and
 * asks the person only when Hume ID needs them; `interactive` asks at once (the sign-in page's button).
 */
export type SsoPrompt = "none" | "interactive" | "login";

/**
 * Where a Hume ID sign-in starts: the server sends the browser on to Hume ID (contract §2.3, §10, §13).
 * `humeOrg` is the organisation being switched to; `invite` an assessment invite's token (§10).
 */
export function ssoStartUrl(
  next: string,
  { prompt, humeOrg, invite }: { prompt?: SsoPrompt; humeOrg?: string; invite?: string } = {},
): string {
  const params = new URLSearchParams({ next });
  if (humeOrg) params.set("hume_org", humeOrg);
  if (prompt) params.set("prompt", prompt);
  if (invite) params.set("invite", invite);
  return `/api/auth/sso/start?${params.toString()}`;
}

/** Leave the app for Hume ID (a full page load; the server redirects). */
export function goToSso(next: string, options?: Parameters<typeof ssoStartUrl>[1]): void {
  window.location.assign(ssoStartUrl(next, options));
}

/**
 * "Switch organisation" (contract §13, row 4): sign in again for that organisation, silently first, and come
 * back to `next`.
 */
export function switchOrganisation(humeOrg: string, next = "/app"): void {
  goToSso(next, { prompt: "none", humeOrg });
}

const SIGNED_OUT_KEY = "hume:signed-out";

/**
 * The person signed out in this tab (contract §13, row 5): don't sign them back in silently with Hume ID
 * until they sign in again. Kept in sessionStorage, so a new tab tries Hume ID again.
 */
export function rememberSignedOut(signedOut: boolean): void {
  try {
    if (signedOut) window.sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    else window.sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // storage blocked: the silent attempt simply isn't suppressed
  }
}

export function signedOutHere(): boolean {
  try {
    return window.sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
}
